import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_dmdtrans` raw rows into
 * `ar_sales_orders` + `ar_so_line_items` domain rows.
 *
 * Stage 2 of the DMDTRANS ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_dmdtrans`. For each row in the latest batch:
 *   - Resolves product SKU → `im_products_v2.id`
 *   - Resolves warehouse code → `im_warehouses.id`
 *   - Upserts into `ar_sales_orders` and `ar_so_line_items`
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_dmdtrans rows considered to those touched by the current run.
 *
 * After the upsert pass it runs a close-reconciliation: the upsert only applies
 * statuses present in the current extract, so orders AS400 has stopped sending
 * stay frozen at `pending`. Any sales order not seen in any DMDTRANS extract
 * within `staleCloseDays` is transitioned to `closed`.
 */
export const transformDmdtransSalesOrdersStep = defineStepConfig({
  type: "transform_dmdtrans_sales_orders",
  name: "Transform DMDTRANS → Sales Orders",
  description:
    "Reads rexel_dmdtrans rows updated by the current run, FK-resolves product + warehouse, and upserts into ar_sales_orders + ar_so_line_items.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    ordersCreated: z
      .number()
      .describe("Number of new ar_sales_orders rows created"),
    ordersUpdated: z
      .number()
      .describe("Number of existing ar_sales_orders rows updated"),
    lineItemsCreated: z
      .number()
      .describe("Number of ar_so_line_items rows created"),
    skippedProductCount: z
      .number()
      .describe(
        "Number of distinct product SKUs skipped because they don't exist in im_products_v2",
      ),
    skippedWarehouseCount: z
      .number()
      .describe(
        "Number of distinct warehouse codes skipped (not found in im_warehouses)",
      ),
    rawRowsProcessed: z
      .number()
      .describe("Total number of rexel_dmdtrans rows processed"),
    staleSalesOrdersClosed: z
      .number()
      .describe(
        "Sales orders transitioned to 'closed' because they haven't appeared in any DMDTRANS extract within staleCloseDays (AS400 stopped reporting them).",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of rows to process per batch"),
    staleCloseDays: z
      .number()
      .min(1)
      .prefault(30)
      .describe(
        "Close-reconciliation window: sales orders not seen in any DMDTRANS extract within this many days are closed. DMDTRANS is a full snapshot, so a dropped-out order is treated as no longer open.",
      ),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
